import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { standardRequest } from 'shared/utils/request';
import { Person, Row } from 'talent_matching/types';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { useQueryParams } from 'talent_matching/hooks/use_people';
import { OPENSEARCH_MAX_RESULT_WINDOW } from 'talent_matching/opensearch_constraints';

// After the current page loads, fetches the next page and:
// 1. Caches the full result under the next page's query key so navigation to it is instant.
// 2. Writes all fetched rows into the current page's buffer so that row exclusions
//    (reject/advance/bookmark) fill the gap without a network request. GREEN-69951
const usePrefetchBuffer = ({
  enabled,
  currentQueryKey,
}: {
  enabled: boolean;
  currentQueryKey: string[];
}) => {
  const { paths } = useTalentMatchingTabContext();
  const queryClient = useQueryClient();
  const { queryParams, filterParams } = useQueryParams();
  const currentQueryKeyStr = JSON.stringify(currentQueryKey);
  // exclude_application_ids lives on filterParams, not on the narrowly-typed queryParams.
  const excludedIdsStr = JSON.stringify(filterParams.exclude_application_ids);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Skip if the next page's starting offset would exceed OpenSearch's result window —
    // the server clamps it back to the last valid page, producing duplicates in the buffer.
    const nextPageFrom = queryParams.page * (queryParams.page_size ?? 0);
    if (nextPageFrom >= OPENSEARCH_MAX_RESULT_WINDOW) {
      return;
    }

    const bufferParams = { ...queryParams, page: queryParams.page + 1 };

    const prefetch = async () => {
      const pathWithSearch = paths.peoplePath + window.location.search;
      const { data } = await standardRequest.get(pathWithSearch, {
        params: bufferParams,
      });

      const allRows: Row[] = (data.applications as Person[]).map((person) => {
        queryClient.setQueryData(['person', person.id], person);
        return { id: person.id };
      });

      // Cache the full next page under its own query key so navigating to it is instant.
      // Exclude exclude_application_ids from the key to match the convention in usePeople.
      // bufferParams carries all runtime filter fields but is narrowly typed as { page, page_size },
      // so cast to surface exclude_application_ids for the destructure.
      const { exclude_application_ids: _excluded, ...nextPageKeyParams } =
        bufferParams as typeof bufferParams & {
          exclude_application_ids?: number[];
        };
      const nextPageQueryKey = ['people', JSON.stringify(nextPageKeyParams)];
      queryClient.setQueryData(nextPageQueryKey, {
        rows: allRows,
        buffer: [],
        totalRows: data.total,
      });

      // Write all fetched rows into the current page's buffer for instant row replacement.
      queryClient.setQueryData<{
        rows: Row[];
        buffer: Row[];
        totalRows: number;
      }>(currentQueryKey, (old) => old && { ...old, buffer: allRows });
    };

    prefetch();
    // currentQueryKeyStr/excludedIdsStr change on page/filter/exclusion changes,
    // keeping the buffer in sync. Other queryParams deps are intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, currentQueryKeyStr, excludedIdsStr]);
};

export default usePrefetchBuffer;
